import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import InputImage from "../project/component/Input/InputImage";
import SignatureCanvas from "react-signature-canvas";
import { FaExclamationCircle, FaCheckCircle } from "react-icons/fa";
import {
    ClipboardPenLine,
    Contact2,
    Loader,
    School,
    University,
    AppWindow,
    UsersIcon,
    Package2,
    GraduationCap
} from "lucide-react";

// ✅ Section Wrapper
const Section = ({ icon, title, color = "yellow", children }) => (
    <div
        className={`border-l-4 border-${color}-400 bg-${color}-50/40 rounded-lg p-4 sm:p-5`}
    >
        <h3
            className={`font-semibold text-base text-${color}-800 mb-2 flex items-center`}
        >
            {icon}&nbsp;{title}
        </h3>
        {children}
    </div>
);

const InputGroup = ({ label, children }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        {children}
    </div>
);

const Error = ({ name }) => (
    <ErrorMessage name={name} component="div">
        {(msg) => (
            <div className="text-red-600 font-bold text-xs mt-1 flex items-center gap-1">
                <FaExclamationCircle className="inline-block" />
                <span>{msg}</span>
            </div>
        )}
    </ErrorMessage>
);

// ✅ Komponen Signature
const Signature = ({ onChangeResult, signedName1 = "Yang Bertanda Tangan" }) => {
    const signaturePad1 = useRef(null);
    const containerRef = useRef(null);
    const [canvasWidth, setCanvasWidth] = useState(0);

    useEffect(() => {
        const handleResize = () => {
            if (containerRef.current) {
                setCanvasWidth(containerRef.current.offsetWidth);
            }
        };

        handleResize(); // set initial width
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleDrawEnd = () => {
        if (!signaturePad1.current.isEmpty()) {
            const dataUrl = signaturePad1.current.getTrimmedCanvas().toDataURL("image/png");
            onChangeResult(dataUrl);
        }
    };

    const clearSignature1 = () => {
        signaturePad1.current.clear();
        onChangeResult(null);
    };

    return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6 mt-6">
            <h3 className="text-lg font-semibold mb-4 text-center">
                {signedName1}
            </h3>

            <div ref={containerRef} className="flex flex-col items-center w-full">
                {canvasWidth > 0 && (
                    <SignatureCanvas
                        ref={signaturePad1}
                        penColor="black"
                        backgroundColor="white"
                        onEnd={handleDrawEnd}
                        canvasProps={{
                            width: canvasWidth,
                            height: (canvasWidth * 0.5), // proporsional 2:1
                            className: "border rounded shadow w-full max-w-md",
                        }}
                    />
                )}

                <button
                    type="button"
                    onClick={clearSignature1}
                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded text-sm mt-4"
                >
                    Ulangi Tanda Tangan
                </button>
            </div>
        </div>
    );
};
// const Signature = ({ onChangeResult, signedName1 = "Yang Bertanda Tangan" }) => {
//     const signaturePad1 = useRef(null);

//     const handleDrawEnd = () => {
//         if (!signaturePad1.current.isEmpty()) {
//             const dataUrl = signaturePad1.current.getTrimmedCanvas().toDataURL("image/png");
//             onChangeResult(dataUrl);
//         }
//     };

//     const clearSignature1 = () => {
//         signaturePad1.current.clear();
//         onChangeResult(null);
//     };

//     return (
//         <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6 mt-6">
//             <h3 className="text-lg font-semibold mb-4 text-center">
//                 {signedName1}
//             </h3>

//             <div className="flex flex-col items-center">
//                 <SignatureCanvas
//                     ref={signaturePad1}
//                     penColor="black"
//                     backgroundColor="white"
//                     onEnd={handleDrawEnd}
//                     canvasProps={{
//                         width: 400,
//                         height: 200,
//                         className: "border rounded shadow w-full max-w-md",
//                     }}
//                 />

//                 <button
//                     type="button"
//                     onClick={clearSignature1}
//                     className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded text-sm mt-4"
//                 >
//                     Ulangi Tanda Tangan
//                 </button>
//             </div>
//         </div>
//     );
// };

const FormRequestApp = () => {
    const { type } = useParams();
    const navigate = useNavigate();

    const [initialValues, setInitialValues] = useState({
        type,
        app_mode: "",
        app_template: "", // 🔹 NEW: pilihan template ready-made
        academic_level: "",
        total_staff: "",
        total_teacher: "",
        total_student: "",
        pic_name: "",
        pic_position: "",
        pic_contact: "",
        pic_email: "",
        pic_password: "",
        logo: null,
        institute_name: "",
        corp_name: "",
        city: "",
        address: "",
        contact_official: "",
        email_official: "",
        web_official: "",
        signature: null,
    });

    // Daftar aplikasi untuk kedua mode
    const appOptions = {
        custom: [
            { value: "akademik", title: "Aplikasi Siakad Premium", description: "Kelola Data Siswa, Guru, Jadwal, Nilai serta Absensi" },
            { value: "pos", title: "Aplikasi Point Of Sales", description: "Kelola Data Penjualan, Produk, Transaksi dan Laporan" },
            {
                value: "personalia", title: "Aplikasi Kepegawaian", description: "Kelola Data Pegawai, Kehadiran, Payroll, dan Arsip",
            },
        ],
        readyMade: [
            { value: "lakademik", title: "L`Akademik", description: 'Kelola Data Siswa, Guru, Jadwal, Nilai serta Absensi' },
            { value: "lpos", title: "L`POS", description: 'Kelola Data Penjualan, Produk, Transaksi dan Laporan' },
            { value: "lpersonalia", title: "L`Personalia", description: 'Kelola Data Pegawai, Kehadiran, Payroll, dan Arsip' },
        ],
    };

    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const messageRef = useRef(null); // 🔹 Tambahkan ref
    const BASE_URL = process.env.REACT_APP_API_BASE_URL;

    useEffect(() => {
        if (successMessage || errorMessage) {
            messageRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [successMessage, errorMessage]);

    const onSubmit = async (values, { setSubmitting, resetForm }) => {
        setErrorMessage(null);
        setSuccessMessage(null);

        try {
            const formData = new FormData();
            Object.entries(values).forEach(([key, value]) => {
                formData.append(key, value);
            });

            const response = await axios.post(
                `${BASE_URL}/api/superapps/project/requestapp`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                }
            );

            if (response.status === 200 || response.status === 201) {
                setSuccessMessage("✅ Form berhasil dikirim!");
                resetForm();
                // Opsional: navigasi setelah 2 detik
                // setTimeout(() => navigate("/detail/request"), 2000);
            } else {
                setErrorMessage(
                    response.data?.message || "Gagal mengirim form, coba lagi."
                );
            }
        } catch (error) {
            console.error("Error submitting form:", error);
            setErrorMessage(
                error.response?.data?.message || "Terjadi kesalahan saat mengirim form."
            );
        } finally {
            setSubmitting(false);
        }
    };

    const validationSchema = Yup.object({
        app_mode: Yup.string().required("Pilih mode aplikasi"),
        app_template: Yup.string().required("Pilih template aplikasi"),
        academic_level: Yup.string().when("app_template", {
            is: (value) => ["akademik", "lakademik"].includes(value),
            then: (schema) => schema.required("Pilih level akademik"),
            otherwise: (schema) => schema.notRequired(),
        }),
        total_staff: Yup.number().required("Isi total staf").positive("Harus bernilai positif"),
        total_teacher: Yup.number().when("app_template", {
            is: (value) => ["akademik", "lakademik"].includes(value),
            then: (schema) =>
                schema.required("Isi total guru/dosen").positive("Harus bernilai positif"),
            otherwise: (schema) => schema.notRequired(),
        }),
        total_student: Yup.number().when("app_template", {
            is: (value) => ["akademik", "lakademik"].includes(value),
            then: (schema) =>
                schema.required("Isi total siswa/mahasiswa").positive("Harus bernilai positif"),
            otherwise: (schema) => schema.notRequired(),
        }),

        logo: Yup.mixed().when("app_mode", {
            is: "custom",
            then: (schema) => schema.required("Logo wajib diisi"),
            otherwise: (schema) => schema.notRequired(),
        }),

        stamp: Yup.mixed().when("app_mode", {
            is: "custom",
            then: (schema) => schema.required("Cap/stempel wajib diisi"),
            otherwise: (schema) => schema.notRequired(),
        }),

        institute_name: Yup.string().when("app_mode", {
            is: "custom",
            then: (schema) => schema.required("Nama institusi wajib diisi"),
            otherwise: (schema) => schema.notRequired(),
        }),

        leader: Yup.string().when("app_mode", {
            is: "custom",
            then: (schema) => schema.required("Nama pimpinan wajib diisi"),
            otherwise: (schema) => schema.notRequired(),
        }),

        city: Yup.string().when("app_mode", {
            is: "custom",
            then: (schema) => schema.required("Kota/Kabupaten wajib diisi"),
            otherwise: (schema) => schema.notRequired(),
        }),

        address: Yup.string().when("app_mode", {
            is: "custom",
            then: (schema) => schema.required("Alamat wajib diisi"),
            otherwise: (schema) => schema.notRequired(),
        }),

        contact_official: Yup.string().when("app_mode", {
            is: "custom",
            then: (schema) => schema.required("Kontak resmi wajib diisi"),
            otherwise: (schema) => schema.notRequired(),
        }),

        email_official: Yup.string().when("app_mode", {
            is: "custom",
            then: (schema) =>
            schema
                .email("Format email tidak valid")
                .required("Email resmi wajib diisi"),
            otherwise: (schema) => schema.notRequired(),
        }),

        web_official: Yup.string().when("app_mode", {
            is: "custom",
            then: (schema) => schema.url("Format URL tidak valid").required("Website resmi wajib diisi"),
            otherwise: (schema) => schema.notRequired(),
        }),

        pic_name: Yup.string().required("Nama PIC wajib diisi"),
        pic_position: Yup.string().required("Jabatan PIC wajib diisi"),
        pic_password: Yup.string().required("Password PIC wajib diisi"),
        corp_name: Yup.string().required("Badan hukum wajib diisi"),
        city: Yup.string().required("Kota/Kabupaten wajib diisi"),
        address: Yup.string().required("Alamat wajib diisi"),
        contact_official: Yup.string().required("Kontak Resmi wajib diisi"),
        email_official: Yup.string()
            .email("Format email tidak valid")
            .required("Email wajib diisi"),
        web_official: Yup.string().url("Format URL tidak valid"),
        signature: Yup.mixed().required("Tanda tangan wajib diisi"),
    });

    return (
        <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
            <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-xl p-6 sm:p-8 border border-gray-100">
                <h2 className="flex items-center justify-center gap-3 text-2xl sm:text-3xl font-bold text-[#f29f02] m-6">
                    <ClipboardPenLine className="w-8 h-8 sm:w-10 sm:h-10" />
                    <span>Form Request Apps</span>
                </h2>

                {/* ✅ Success & Error Alert */}
                <div ref={messageRef}>
                    {successMessage && (
                        <div className="mb-4 bg-green-50 border border-green-400 text-green-700 p-3 rounded-md text-sm flex items-center gap-2">
                            <FaCheckCircle />
                            <span>{successMessage}</span>
                        </div>
                    )}
                    {errorMessage && (
                        <div className="mb-4 bg-red-50 border border-red-300 text-red-700 p-3 rounded-md text-sm flex items-start gap-2">
                            <FaExclamationCircle className="mt-0.5" />
                            <span>{errorMessage}</span>
                        </div>
                    )}
                </div>

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={onSubmit}
                    enableReinitialize
                >
                    {({ setFieldValue, values, isSubmitting }) => (
                        <Form className="space-y-5">
                            <Section icon={<AppWindow className="w-5 h-5" />} title="Mode Aplikasi" color="blue">
                                <Field
                                    as="select"
                                    name="app_mode"
                                    className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-300"
                                >
                                    <option value="">-- Pilih Mode Aplikasi --</option>
                                    <option value="Custom Apps">Custom Apps</option>
                                    <option value="Ready-made Apps">Ready-made Apps</option>
                                </Field>
                                <Error name="app_mode" />
                            </Section>

                            {/* 🔹 Jika mode Custom atau Ready-made Apps, tampilkan pilihan aplikasi */}
                            {["Custom Apps", "Ready-made Apps"].includes(values.app_mode) && (
                                <Section
                                    icon={<Package2 className="w-5 h-5" />}
                                    title={
                                        values.app_mode === "Ready-made Apps"
                                            ? "Pilih Template Ready-Made"
                                            : "Pilih Jenis Aplikasi Custom"
                                    }
                                    color={values.app_mode === "Ready-made Apps" ? "emerald" : "amber"}
                                >
                                    <Field
                                        as="select"
                                        name="app_template"
                                        className={`w-full p-2 border rounded-md text-sm focus:ring-2 ${values.app_mode === "Ready-made Apps"
                                            ? "focus:ring-emerald-300"
                                            : "focus:ring-amber-300"
                                            }`}
                                    >
                                        <option value="">-- Pilih Aplikasi --</option>
                                        {(values.app_mode === "Ready-made Apps"
                                            ? appOptions.readyMade
                                            : appOptions.custom
                                        ).map((app) => (
                                            <option key={app.value} value={app.value}>
                                                {app.title ? `${app.title} — ${app.description}` : ""}
                                            </option>
                                        ))}
                                    </Field>
                                    <Error name="app_template" />

                                    {values.app_template && (
                                        <div className="mt-4 p-3 bg-emerald-50 border border-emerald-300 rounded-lg text-sm">
                                            <h3 className="font-bold">
                                                {(values.app_mode === "Ready-made Apps"
                                                    ? appOptions.readyMade
                                                    : appOptions.custom
                                                ).find((a) => a.value === values.app_template)?.title}
                                            </h3>

                                            <p>
                                                {(values.app_mode === "Ready-made Apps"
                                                    ? appOptions.readyMade
                                                    : appOptions.custom
                                                ).find((a) => a.value === values.app_template)?.description}
                                            </p>
                                        </div>
                                    )}
                                </Section>
                            )}

                            {/* Bagian Form */}
                            {["akademik", "lakademik"].includes(values.app_template) && (
                                <Section
                                    icon={<GraduationCap className="w-5 h-5" />}
                                    title="Pengaturan Akademik"
                                    color="indigo"
                                >
                                    {/* Level Akademik */}
                                    <Section icon={<School className="w-5 h-5" />} title="Level Akademik">
                                        <Field
                                            as="select"
                                            name="academic_level"
                                            className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-[#f29f02]"
                                        >
                                            <option value="">-- Pilih Level Akademik --</option>
                                            <option value="Pendidikan Dasar">Pendidikan Dasar</option>
                                            <option value="Pendidikan Menengah">Pendidikan Menengah</option>
                                            <option value="Pendidikan Tinggi">Pendidikan Tinggi</option>
                                        </Field>
                                        <Error name="academic_level" />
                                    </Section>

                                    {/* Total Civitas Akademika */}
                                    <Section icon={<UsersIcon className="w-5 h-5" />} title="Total Civitas Akademika">
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                            {[
                                                { label: "Staf", name: "total_staff" },
                                                { label: "Guru/Dosen", name: "total_teacher" },
                                                { label: "Siswa/Mahasiswa", name: "total_student" },
                                            ].map(({ label, name }) => (
                                                <InputGroup key={name} label={label}>
                                                    <Field
                                                        type="number"
                                                        name={name}
                                                        className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-gray-300"
                                                    />
                                                    <Error name={name} />
                                                </InputGroup>
                                            ))}
                                        </div>
                                    </Section>
                                </Section>
                            )}

                            {/* Stakeholder */}
                            <Section icon={<Contact2 className="w-5 h-5" />} title="Stakeholder">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {[
                                        { label: "PIC Nama", name: "pic_name" },
                                        { label: "PIC Jabatan", name: "pic_position" },
                                        { label: "PIC Kontak", name: "pic_contact" },
                                        { label: "PIC Email", name: "pic_email" },
                                        { label: "PIC Password", name: "pic_password", type: "password" },
                                        { label: "Badan Hukum", name: "corp_name" },
                                    ].map(({ label, name, type }) => (
                                        <InputGroup key={name} label={label}>
                                            <Field
                                                type={type || "text"}
                                                name={name}
                                                className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-green-300"
                                            />
                                            <Error name={name} />
                                        </InputGroup>
                                    ))}
                                </div>
                            </Section>

                            {/* Data Institusi */}
                            {["Custom Apps"].includes(values.app_mode) && (
                                <Section icon={<University className="w-5 h-5" />} title="Data Institusi">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="flex flex-col items-center border-2 border-dashed border-yellow-300 rounded-lg p-3 bg-white hover:bg-yellow-50 transition">
                                            <InputImage
                                                attr={{
                                                    name: "logo",
                                                    placeholder: "Logo Institusi",
                                                    height: 180,
                                                    width: 180,
                                                }}
                                                handleCroppedFoto={(v) => {
                                                    console.log(v);
                                                    setFieldValue("logo", v);
                                                }}
                                            />
                                            <InputImage
                                                attr={{
                                                    name: "stamp",
                                                    placeholder: "Stempel Institusi",
                                                    height: 180,
                                                    width: 180,
                                                }}
                                                handleCroppedFoto={(v) => {
                                                    setFieldValue("stamp", v);
                                                }}
                                            />
                                        </div>

                                        <div className="space-y-3">
                                            {[
                                                { label: "Nama Institusi", name: "institute_name" },
                                                { label: "Nama Pimpinan", name: "leader" },
                                                { label: "Kota/Kabupaten", name: "city" },
                                                { label: "Alamat", name: "address", as: "textarea" },
                                                { label: "Telp. Kantor", name: "contact_official" },
                                                { label: "Email Resmi", name: "email_official", type: "email" },
                                                { label: "Website Resmi", name: "web_official", type: "url" },
                                            ].map(({ label, name, as, type }) => (
                                                <InputGroup key={name} label={label}>
                                                    <Field
                                                        as={as || "input"}
                                                        type={type || "text"}
                                                        name={name}
                                                        className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-yellow-300"
                                                    />
                                                    <Error name={name} />
                                                </InputGroup>
                                            ))}
                                        </div>
                                    </div>
                                </Section>
                            )}

                            {/* Signature */}
                            <Signature
                                signedName1="Tanda Tangan Pemohon"
                                onChangeResult={(sig) => setFieldValue("signature", sig)}
                            />
                            <Error name="signature" />

                            {/* Tombol Submit */}
                            <div className="text-center sm:text-right pt-4">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md text-sm font-medium transition disabled:opacity-60 flex items-center gap-2 mx-auto sm:mx-0"
                                >
                                    {isSubmitting ? (
                                        <div className="flex items-center gap-2">
                                            <Loader className="animate-spin w-4 h-4" />
                                            <span>Mengirim...</span>
                                        </div>
                                    ) : (
                                        <>Simpan</>
                                    )}
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default FormRequestApp;
